import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button, Checkbox, Dropdown, Input } from 'components/UIElements'
import { modalActions } from 'store/modal'
import { tmgEditSequenceIcon, tmgSequenceLockedIcon } from 'assets/assets'
import STRINGS from 'utils/strings'
import {
  QUESTION_TYPE_MAP,
  TIMED_MATCHING_GRID_DEFAULTS,
  TIMED_MATCHING_GRID_TIMER_DISPLAY_OPTIONS,
} from 'utils/constants'
import {
  applyTimedMatchingGridDetailsPatch,
  clampGridDimension,
  countPlacedItems,
  getGridCellCount,
  getSizingConstraintNote,
  getTimedMatchingGridDetails,
  getTimedMatchingGridItemSequenceWarning,
  hasCompleteSymbolKey,
} from 'utils/timedMatchingGrid'
import ItemSequenceEditor from '../TimedMatchingGrid/ItemSequenceEditor'
import './TimedMatchingGridConfigPanel.scss'

const TimedMatchingGridConfigPanel = ({
  item,
  itemId,
  updateItem,
  disabled,
  errors = {},
  openModal,
  closeModal,
  children,
}) => {
  if (!item || item.type !== QUESTION_TYPE_MAP.timedMatchingGrid) return null

  const details = getTimedMatchingGridDetails(item)
  const keyComplete = hasCompleteSymbolKey(details.symbol_key)
  const sequenceTotal = getGridCellCount(details.grid_rows, details.grid_columns)
  const sequencePlaced = countPlacedItems(details.item_sequence)
  const sequenceWarning = getTimedMatchingGridItemSequenceWarning(details)
  const timeLimitValue = Number(details.time_limit_seconds)
  const timeLimitErr =
    Number.isInteger(timeLimitValue) && timeLimitValue > TIMED_MATCHING_GRID_DEFAULTS.timeLimitSecondsMax
      ? STRINGS.tmgTimeLimitMaxErr
      : Number.isInteger(timeLimitValue) && timeLimitValue < TIMED_MATCHING_GRID_DEFAULTS.timeLimitSecondsMin
        ? STRINGS.tmgTimeLimitMinErr
        : errors.tmgTimeLimitErr

  const updateDetails = (patch, options) => {
    if (!updateItem) return
    const nextDetails = applyTimedMatchingGridDetailsPatch(details, patch, options)
    updateItem(itemId, {
      ...item,
      attributes: {
        ...item.attributes,
        in_review: false,
        timed_matching_grid_details: nextDetails,
      },
    })
  }

  const onStepDimension = (key, delta) => {
    const current = Number(details[key]) || TIMED_MATCHING_GRID_DEFAULTS.gridRows
    updateDetails({ [key]: clampGridDimension(current + delta) })
  }

  const onTimeLimitChange = value => {
    if (value === '') {
      updateDetails({ time_limit_seconds: '' })
      return
    }
    const next = parseInt(value, 10)
    if (!Number.isInteger(next)) return
    const prev = Number(details.time_limit_seconds)
    const isStepUp = Number.isInteger(prev) && next === prev + 1
    if (isStepUp && next > TIMED_MATCHING_GRID_DEFAULTS.timeLimitSecondsMax) return
    updateDetails({ time_limit_seconds: next })
  }

  const openItemSequenceScreen = () => {
    openModal({
      className: 'tmg-item-sequence-modal',
      noButtons: true,
      hideClose: true,
      closeOnBackgroundClick: false,
      content: (
        <ItemSequenceEditor
          details={details}
          disabled={disabled || !keyComplete}
          errors={errors}
          onChangeSequence={seq => updateDetails({ item_sequence: seq })}
          onClose={closeModal}
        />
      ),
    })
  }

  return (
    <div className='tmg-config-panel attribute panel'>
      <div className='tmg-config-row'>
        <div className='tmg-config-field'>
          <h5 className='label-small med-font'>{STRINGS.tmgGridRows}</h5>
          <div className='tmg-stepper'>
            <Button
              className='inline flexed'
              circle
              no-margin
              icon='fas fa-minus'
              disabled={disabled || details.grid_rows <= TIMED_MATCHING_GRID_DEFAULTS.gridMin}
              onClick={() => onStepDimension('grid_rows', -1)}
            />
            <p className='inline flexed'>{details.grid_rows}</p>
            <Button
              className='inline flexed'
              circle
              no-margin
              icon='fas fa-plus'
              disabled={disabled || details.grid_rows >= TIMED_MATCHING_GRID_DEFAULTS.gridMax}
              onClick={() => onStepDimension('grid_rows', 1)}
            />
          </div>
        </div>

        <div className='tmg-config-field'>
          <h5 className='label-small med-font'>{STRINGS.tmgGridColumns}</h5>
          <div className='tmg-stepper'>
            <Button
              className='inline flexed'
              circle
              no-margin
              icon='fas fa-minus'
              disabled={disabled || details.grid_columns <= TIMED_MATCHING_GRID_DEFAULTS.gridMin}
              onClick={() => onStepDimension('grid_columns', -1)}
            />
            <p className='inline flexed'>{details.grid_columns}</p>
            <Button
              className='inline flexed'
              circle
              no-margin
              icon='fas fa-plus'
              disabled={disabled || details.grid_columns >= TIMED_MATCHING_GRID_DEFAULTS.gridMax}
              onClick={() => onStepDimension('grid_columns', 1)}
            />
          </div>
        </div>
      </div>

      <p className='tmg-sizing-note'>{getSizingConstraintNote(details.grid_rows, details.grid_columns)}</p>

      <div className='tmg-config-row'>
        <div className='tmg-config-field'>
          <h5 className='label-small med-font'>{STRINGS.tmgTimeLimit}</h5>
          <Input
            type='number'
            min={0}
            disabled={disabled}
            value={details.time_limit_seconds}
            hasError={!!timeLimitErr}
            errorText={timeLimitErr}
            wrapError
            onChange={onTimeLimitChange}
          />
        </div>

        <div className='tmg-config-field'>
          <h5 className='label-small med-font'>{STRINGS.tmgTimerDisplay}</h5>
          <Dropdown
            selected={details.timer_display}
            options={TIMED_MATCHING_GRID_TIMER_DISPLAY_OPTIONS}
            disabled={disabled}
            onSelect={opt => updateDetails({ timer_display: opt.value })}
          />
        </div>
      </div>

      {children}

      <div className='tmg-randomize-field tmg-config-field'>
        <h5 className='label-small med-font'>{STRINGS.tmgRandomization}</h5>
        <Checkbox
          toggle
          disabled={disabled}
          checked={!!details.randomize_items}
          label={STRINGS.tmgRandomize}
          onClick={() =>
            updateDetails({ randomize_items: !details.randomize_items }, { clearSequenceOnRandomize: true })
          }
        />
      </div>

      {!details.randomize_items && (
        <div className='tmg-item-sequence-entry'>
          <div className='tmg-item-sequence-entry-copy'>
            <h5 className='label-small med-font'>{STRINGS.tmgItemSequenceTitle}</h5>
            <p className='tmg-helper'>
              {sequencePlaced} of {sequenceTotal} {STRINGS.tmgItemSequenceIncomplete}
              {sequenceWarning ? ` — ${sequenceWarning}` : ''}
            </p>
          </div>
          {!disabled && (
            <>
              <Button
                link
                no-margin
                className='tmg-edit-item-sequence'
                content={
                  <>
                    <span className='tmg-edit-item-sequence-icon'>{tmgEditSequenceIcon}</span>
                    {STRINGS.tmgEditItemSequence}
                  </>
                }
                disabled={!keyComplete}
                onClick={openItemSequenceScreen}
              />
              {!keyComplete && (
                <p className='tmg-item-sequence-locked'>
                  <span className='tmg-item-sequence-locked-icon'>{tmgSequenceLockedIcon}</span>
                  <span>{STRINGS.tmgEditItemSequenceLocked}</span>
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

TimedMatchingGridConfigPanel.propTypes = {
  children: PropTypes.node,
  closeModal: PropTypes.func,
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    tmgItemSequenceErr: PropTypes.string,
    tmgTimeLimitErr: PropTypes.string,
  }),
  item: PropTypes.shape({
    type: PropTypes.string,
    attributes: PropTypes.shape({
      timed_matching_grid_details: PropTypes.shape({}),
    }),
  }),
  itemId: PropTypes.string,
  openModal: PropTypes.func,
  updateItem: PropTypes.func,
}

const mapState = (state, ownProps) => ({
  errors:
    state.surveyEditor?.surveyErrors?.questions?.[ownProps.itemId] ||
    state.clinroEditor?.clinroErrors?.questions?.[ownProps.itemId] ||
    {},
})

const mapDispatch = {
  openModal: modalActions.openModal,
  closeModal: modalActions.closeModal,
}

export default connect(mapState, mapDispatch)(TimedMatchingGridConfigPanel)
